/**
 * Free delivery bar, cart drawer runtime (custom, not part of stock Horizon, 4.1.5 compatible).
 *
 * Loaded by sections/cart-tracker-drawer.liquid on every page except /cart,
 * and only when the cart type is "drawer". Horizon's drawer has no block area, so
 * this copies the bar out of #cart-tracker-template into the drawer and keeps it there.
 *
 * Guards:
 *   - Runs once per page. A second copy of the script (the theme editor re-rendering
 *     the section) exits; the first copy picks up new settings on shopify:section:load.
 *   - Inserts the bar only when the drawer doesn't already contain one.
 *   - Horizon morphs the drawer after every cart change, which can drop the bar.
 *     A MutationObserver puts it back. Repaints are keyed on the cart state, so the
 *     observer never reacts to its own writes in a loop.
 *
 * No @theme/* or @shopify/* imports, so a change to Horizon's import map can't break it.
 * Depends on stock markup: #cart-drawer, .cart-drawer__content, .cart-drawer__items,
 * and the shopify:cart:lines-update / shopify:cart:discount-update events.
 */
(() => {
  if (window.cartTrackerDrawer) return;
  window.cartTrackerDrawer = true;

  const CONFIG_ID = 'cart-tracker-config';
  const TEMPLATE_ID = 'cart-tracker-template';
  const BAR = '.cart-tracker';
  const DRAWER = '#cart-drawer';
  const DRAWER_SECTION = 'shopify-section-cart-drawer-section';
  const HOST = '.cart-drawer__content';
  const ANCHOR = '.cart-drawer__items';

  /** Minor-unit precision for currencies that don't use two decimals. Mirrors assets/money-formatting.js. */
  const CURRENCY_DECIMALS = {
    BHD: 3, BIF: 0, BYR: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0, JOD: 3, JPY: 0,
    KMF: 0, KRW: 0, KWD: 3, LYD: 3, MRO: 5, OMR: 3, PYG: 0, RWF: 0, TND: 3, UGX: 0, UYI: 0,
    UYW: 4, VND: 0, VUV: 0, XAF: 0, XAG: 0, XAU: 0, XBA: 0, XBB: 0, XBC: 0, XBD: 0, XDR: 0,
    XOF: 0, XPD: 0, XPF: 0, XPT: 0, XSU: 0, XTS: 0, XUA: 0,
  };

  /** [thousands separator, decimal separator, fixed precision] per Shopify money placeholder. */
  const MONEY_PLACEHOLDERS = {
    amount: [',', '.'],
    amount_no_decimals: [',', '.', 0],
    amount_with_comma_separator: ['.', ','],
    amount_no_decimals_with_comma_separator: ['.', '.', 0],
    amount_no_decimals_with_space_separator: [' ', '.', 0],
    amount_with_space_separator: [' ', ','],
    amount_with_period_and_space_separator: [' ', '.'],
    amount_with_apostrophe_separator: ["'", '.'],
  };

  let config = readConfig();
  let total = Number(config?.total) || 0;
  let itemCount = Number(config?.itemCount) || 0;

  /** Bumped when the editor reloads the section, so existing bars repaint with new settings. */
  let revision = 0;
  let frame = 0;

  const observer = new MutationObserver(schedule);

  function readConfig() {
    const element = document.getElementById(CONFIG_ID);
    if (!element) return null;

    try {
      const data = JSON.parse(element.textContent || '');
      return data;
    } catch {
      return null;
    }
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      sync();
    });
  }

  /** Makes sure the drawer holds exactly one up-to-date bar, or none when the section is gone. */
  function sync() {
    const drawer = document.querySelector(DRAWER);
    if (!drawer) return;

    if (!config) {
      drawer.querySelectorAll(BAR).forEach((bar) => bar.remove());
      return;
    }

    const host = drawer.querySelector(HOST);
    if (!host) return;

    let bar = host.querySelector(`:scope > ${BAR}`);

    if (!(bar instanceof HTMLElement)) {
      const template = document.getElementById(TEMPLATE_ID);
      if (!(template instanceof HTMLTemplateElement)) return;

      bar = template.content.querySelector(BAR)?.cloneNode(true);
      if (!(bar instanceof HTMLElement)) return;

      const anchor = host.querySelector(`:scope > ${ANCHOR}`);
      if (anchor) anchor.before(bar);
      else host.prepend(bar);
    }

    paint(bar);
  }

  /** @param {HTMLElement} bar */
  function paint(bar) {
    const key = `${total}|${itemCount}|${revision}`;
    if (bar.dataset.cartTrackerKey === key) return;
    bar.dataset.cartTrackerKey = key;

    // Resolved in Liquid (number setting or Market metafield), in minor units.
    const threshold = Number(bar.dataset.cartTrackerThreshold) || 0;
    const remaining = Math.max(threshold - total, 0);
    const progress = threshold > 0 ? Math.min(Math.max(total / threshold, 0), 1) : 1;

    bar.hidden = Boolean(config.hideWhenEmpty) && itemCount === 0;
    bar.dataset.state = remaining === 0 ? 'qualified' : 'progress';
    bar.style.setProperty('--cart-tracker-progress', String(progress));

    const message = bar.querySelector('[data-cart-tracker-message]');
    if (!message) return;

    // Texts arrive already HTML-escaped from Liquid (setting | escape, or the `t` filter).
    const text = remaining === 0 ? bar.dataset.cartTrackerQualified : bar.dataset.cartTrackerProgress;
    message.innerHTML = String(text ?? '').replaceAll(
      '[amount]',
      `<strong class="cart-tracker__amount">${escapeHtml(formatMoney(remaining))}</strong>`
    );
  }

  /** @param {string} currency */
  function currencyPrecision(currency) {
    return CURRENCY_DECIMALS[String(currency).toUpperCase()] ?? 2;
  }

  /**
   * Same output as Liquid's `money` filter, e.g. "€{{amount_with_comma_separator}}" -> "€39,21".
   * @param {number} minorUnits
   */
  function formatMoney(minorUnits) {
    const currency = config.currency || 'EUR';
    const precision = currencyPrecision(currency);
    const divisor = 10 ** precision;

    return String(config.moneyFormat || '{{amount}}').replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (placeholder === 'currency') return currency;

      const [thousands, decimal, places = precision] = MONEY_PLACEHOLDERS[placeholder] ?? [',', '.'];
      const [whole = '0', fraction = ''] = (minorUnits / divisor).toFixed(places).split('.');
      const grouped = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousands);

      return places <= 0 ? grouped : grouped + decimal + fraction.padEnd(places, '0');
    });
  }

  /** @param {unknown} value */
  function escapeHtml(value) {
    return String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  }

  /** Reads the cart straight from Shopify when an event doesn't carry one. */
  async function refreshFromCart() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      total = Number(cart.total_price) || 0;
      itemCount = Number(cart.item_count) || 0;
    } catch {
      /* keep the last known state */
    }
    schedule();
  }

  /**
   * Horizon's cart events carry a promise that resolves with the updated cart.
   * `cart.cost.totalAmount.amount` is a decimal string ("39.21"); `cart` can be null.
   * @param {Event & { promise?: Promise<any> }} event
   */
  function onCartEvent(event) {
    if (typeof event.promise?.then !== 'function') {
      refreshFromCart();
      return;
    }

    event.promise
      .then((result) => {
        const amount = result?.cart?.cost?.totalAmount;
        if (!amount) {
          refreshFromCart();
          return;
        }

        const precision = currencyPrecision(amount.currencyCode || config?.currency || 'EUR');
        total = Math.round(parseFloat(amount.amount) * 10 ** precision) || 0;
        itemCount = Number(result.cart.totalQuantity) || 0;
        schedule();
      })
      .catch(() => {
        /* the theme reports cart errors itself; the cart didn't change */
      });
  }

  /** @param {Event} event */
  function isOwnSection(event) {
    return event.target instanceof Element && Boolean(event.target.querySelector(`#${CONFIG_ID}`));
  }

  /** Theme editor: re-read settings and rebuild the bar from the fresh template. */
  function reloadFromEditor() {
    config = readConfig();
    revision += 1;
    document.querySelector(DRAWER)?.querySelectorAll(BAR).forEach((bar) => bar.remove());
    schedule();
  }

  document.addEventListener('shopify:cart:lines-update', onCartEvent);
  document.addEventListener('shopify:cart:discount-update', onCartEvent);

  // A page restored from the back/forward cache may show a cart that changed on another page.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshFromCart();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (isOwnSection(event)) reloadFromEditor();
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Unload fires before the editor removes the section, so wait a frame for it to be gone.
    if (isOwnSection(event)) requestAnimationFrame(reloadFromEditor);
  });

  const observed = document.getElementById(DRAWER_SECTION) ?? document.querySelector(DRAWER);
  if (observed) observer.observe(observed, { childList: true, subtree: true });

  sync();
})();
